import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import SEO from '../components/SEO';
import FastOptimizedImage from '../components/OptimizedImage';
import DownloadButton from '../components/DownloadButton';
import useImagePreloader from '../hooks/useImagePreloader';

const AwardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  margin: 0 0 3rem 0;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const CategorySection = styled(motion.section)`
  margin-bottom: 4rem;
`;

// Simple optimized media grid
const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }
`;

const MediaItem = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.15);
  }
`;

const MediaContent = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 180px;
  }
`;

// Using FastOptimizedImage component instead of styled img for better performance

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  .icon {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  }
  
  .text {
    font-weight: 600;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
  }
  
  .play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    .play-button {
      font-size: 3rem;
      color: white;
      text-shadow: 0 2px 4px rgba(0,0,0,0.7);
      filter: drop-shadow(0 2px 8px rgba(0,0,0,0.5));
      transform: scale(1);
      transition: transform 0.2s ease;
    }
    
    &:hover .play-button {
      transform: scale(1.1);
    }
  }
  
  &:hover .play-overlay {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const MediaCaption = styled.div`
  padding: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #2d3748;
    line-height: 1.3;
  }
  
  p {
    margin: 0;
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.4;
  }
`;

// Gallery modal styles
const GalleryModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const GalleryContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  
  .image-gallery {
    background: transparent;
  }
  
  .image-gallery-slide {
    text-align: center;
  }
  
  .image-gallery-image {
    max-height: 60vh;
    object-fit: contain;
    border-radius: 8px;
  }
  
  .image-gallery-description {
    position: static !important;
    bottom: auto !important;
    left: auto !important;
    right: auto !important;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-top: 1rem;
    max-height: 15vh;
    overflow-y: auto;
    font-size: 0.9rem;
    line-height: 1.4;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  .image-gallery-thumbnails {
    padding: 1rem 0;
  }
  
  .image-gallery-thumbnail {
    border: 2px solid transparent;
    border-radius: 4px;
    
    &.active {
      border-color: #4299e1;
    }
  }
  
  @media (max-width: 768px) {
    .image-gallery-thumbnails {
      display: none;
    }
    
    .image-gallery-description {
      position: static !important;
      bottom: auto !important;
      left: auto !important;
      right: auto !important;
      font-size: 0.8rem;
      padding: 0.5rem 0.75rem;
      max-height: 12vh;
      margin-top: 0.75rem;
    }
    
    .image-gallery-image {
      max-height: 55vh;
    }
  }
`;

const NextSectionContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin: 3rem auto 2rem auto;
  text-align: center;
  max-width: 600px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
`;

const NextSectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

const mediaItems = [
  // Award ceremony photos - using medium-resolution for gallery with thumbnails for grid
  { filename: "Eliza with her award.jpeg", caption: "Eliza Jane Soward proudly holding her Life Saving Award from the Saratoga Springs Fire Department", type: "image", localUrl: "/uploads/elizas-award/ceremony/Eliza with her award.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/Eliza with her award.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/Eliza with her award.jpeg", section: 'ceremony' },
  { filename: "IMG_9276.jpeg", caption: "Engineer David Perry, Captain (Seis) Wetherell, Eliza, Paramedic Abby Walz (lead), Captain Ryan Rackman, Firefighter/AEMT Alec Olsen", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_9276.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_9276.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9276.jpeg", section: 'ceremony' },
  { filename: "IMG_9278.jpeg", caption: "Captain Ryan Rackman and Scott", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_9278.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_9278.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9278.jpeg", section: 'ceremony' },
  { filename: "IMG_9279.jpeg", caption: "Violet and Maggie getting a ride home", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_9279.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_9279.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9279.jpeg", section: 'ceremony' },
  { filename: "IMG_9352.jpeg", caption: "Deputy Chief Kenny Johnson, Captain Ryan Rackman, Firefighter/AEMT Alec Olsen, Engineer Ivan Radcliff, Critical Care Paramedic Isaac Ostler, Eliza Soward, Firefighter/EMT Josh Cluff, Captain Seis Wetherell, Engineer David Perry, Paramedic Abby Walz (lead). Award ceremony celebration, Saratoga Springs City Council with Major Jim Miller", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_9352.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_9352.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9352.jpeg", section: 'ceremony' },
  { filename: "IMG_9368.jpeg", caption: "Family gathering for the honor", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_9368.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_9368.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9368.jpeg", section: 'ceremony' },
  { filename: "C2B04332.jpeg", caption: "Meeting with the fire department team", type: "image", localUrl: "/uploads/elizas-award/first-responders/C2B04332.jpeg", mediumUrl: "/uploads/elizas-award/medium/first-responders/C2B04332.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/first-responders/C2B04332.jpeg", section: 'backstory' },
  { filename: "C2B04335.jpeg", caption: "Emergency responders by the BOX", type: "image", localUrl: "/uploads/elizas-award/first-responders/C2B04335.jpeg", mediumUrl: "/uploads/elizas-award/medium/first-responders/C2B04335.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/first-responders/C2B04335.jpeg", section: 'backstory' },
  { filename: "C2B04340.jpeg", caption: "Heroes of November 28th", type: "image", localUrl: "/uploads/elizas-award/first-responders/C2B04340.jpeg", mediumUrl: "/uploads/elizas-award/medium/first-responders/C2B04340.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/first-responders/C2B04340.jpeg", section: 'backstory' },
  { filename: "C2B04374.jpeg", caption: "Shaking hands with Captain Seis", type: "image", localUrl: "/uploads/elizas-award/first-responders/C2B04374.jpeg", mediumUrl: "/uploads/elizas-award/medium/first-responders/C2B04374.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/first-responders/C2B04374.jpeg", section: 'backstory' },
  { filename: "IMG_3076.jpeg", caption: "Discussing the event together", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_3076.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_3076.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_3076.jpeg", section: 'ceremony' },
  { filename: "IMG_3101.jpeg", caption: "Hearing from Captain Rackman of how the event positively impacted his life", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_3101.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_3101.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_3101.jpeg", section: 'ceremony' },
  { filename: "IMG_3122.jpeg", caption: "Deputy Chief Kenny Johnson", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_3122.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_3122.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_3122.jpeg", section: 'ceremony' },
  { filename: "IMG_3125.jpeg", caption: "Meeting our heroes", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_3125.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_3125.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_3125.jpeg", section: 'ceremony' },
  { filename: "IMG_3128.jpeg", caption: "Meeting with the fire department team", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_3128.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_3128.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_3128.jpeg", section: 'ceremony' },
  { filename: "IMG_9273.jpeg", caption: "Ike and Atziri heading out", type: "image", localUrl: "/uploads/elizas-award/ceremony/IMG_9273.jpeg", mediumUrl: "/uploads/elizas-award/medium/ceremony/IMG_9273.webp", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9273.jpeg", section: 'ceremony' },
  
  // Videos - keeping on S3 for optimal delivery of large files, with local thumbnails
  { filename: "IMG_3103.mov", caption: "Discussing Miracles", type: "video", youtubeUrl: "https://youtu.be/N5OF6ckeW-w", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_3103.jpeg", section: 'ceremony' },
  { filename: "IMG_9282.mov", caption: "Riding home in style", type: "video", youtubeUrl: "https://youtu.be/jvwgXN-3jvQ", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9282.jpeg", section: 'ceremony' },
  { filename: "IMG_9353.mov", caption: "Recognition ceremony highlights with the Major: Event of the Year", type: "video", youtubeUrl: "https://youtu.be/Xv3toZiAlbQ", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_9353.jpeg", section: 'ceremony' },
  { filename: "IMG_0942.mov", caption: "Additional recognition ceremony moments", type: "video", youtubeUrl: "https://youtube.com/shorts/rsnX6r2R0qk", thumbnailUrl: "/uploads/elizas-award/thumbs/ceremony/IMG_0942.jpeg", section: 'ceremony' },
  { filename: "chattingWithCaptainSeisWetherell.mov", caption: "Conversation with Captain Seis Wetherell about the life-saving response", type: "video", youtubeUrl: "https://youtu.be/_P6GxCsBA34?si=ukQ1C3uFUbEpw5nS", thumbnailUrl: "/uploads/elizas-award/thumbs/first-responders/chattingWithCaptainSeisWetherell.jpeg", section: 'backstory' }
];

const mediaCategories = [
  {
    title: "Eliza's Life Saving Award",
    
    description: "Official recognition ceremony and the heroic story behind the award",
    photos: mediaItems // All photos and videos in one section
  }
];

const ElizasAward = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  // Initialize intelligent image preloading for fast Gallery-style performance
  const { preloadOnHover } = useImagePreloader({
    images: mediaItems,
    currentIndex: galleryIndex,
    preloadCount: 3,
    enabled: true
  });

  // Prepare gallery items for React Image Gallery
  const prepareGalleryItems = useCallback(() => {
    return mediaItems.map(item => {
      // Use thumbnail URLs for faster grid loading
      const thumbnailUrl = item.thumbnailUrl;
      // For images: Use medium-resolution as gallery default (60x smaller than full-size)
      // For videos: Use YouTube URL if available, otherwise use S3 URL
      const originalUrl = item.type === 'image'
        ? (item.mediumUrl || item.localUrl) // Medium first, fallback to full-size
        : (item.youtubeUrl || item.s3Url);

      return {
        original: originalUrl,
        thumbnail: thumbnailUrl || originalUrl, // Fallback to original if no thumbnail
        description: item.caption,
        originalAlt: item.caption,
        thumbnailAlt: item.caption,
        originalClass: item.type === 'video' ? 'video-item' : 'image-item',
        isVideo: item.type === 'video',
        videoSrc: item.type === 'video' ? (item.youtubeUrl || item.s3Url) : null,
        isYoutube: item.type === 'video' && !!item.youtubeUrl,
        poster: item.type === 'video' ? item.thumbnailUrl : undefined, // Video poster for better UX
        // Store full-size URL for progressive loading (future enhancement)
        fullSizeUrl: item.type === 'image' ? item.localUrl : null,
        // Include download information for DownloadButton
        filename: item.filename,
        section: item.section || 'ceremony', // Default section for category
        mediaType: item.type
      };
    });
  }, []);

  // Update gallery items when section changes
  useEffect(() => {
    setGalleryItems(prepareGalleryItems());
  }, [prepareGalleryItems]);

  const openGallery = useCallback((index) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  }, []);

  const closeGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  // Simple keyboard navigation for gallery
  useEffect(() => {
    if (!isGalleryOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeGallery();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, closeGallery]);

  return (
    <AwardContainer>
      <SEO 
        title="Eliza's Life Saving Award"
        description="Honoring 17-year-old Eliza Jane Soward who saved her mother's life with CPR on Thanksgiving Day 2024. Photos and videos from the award ceremony recognizing her heroic actions during a medical emergency."
        url="/elizas-award"
        image="/uploads/Eliza with her award.jpeg"
        type="article"
      />
      <PageHeader>
        <PageTitle>Eliza's Life Saving Award</PageTitle>
        <PageSubtitle>
          Honoring the heroic actions of 17-year-old Eliza Jane Soward, who saved her mother's life with CPR on November 28, 2024
        </PageSubtitle>
      </PageHeader>

      {mediaCategories.map((category, categoryIndex) => (
        <CategorySection
          key={categoryIndex}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
          viewport={{ once: true }}
        >
          <MediaGrid>
            {category.photos.map((media, photoIndex) => {
              const globalIndex = mediaItems.findIndex(item => item.filename === media.filename);
              return (
                <MediaItem
                  key={`${media.filename}-${photoIndex}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: Math.min(photoIndex * 0.02, 0.3) }}
                  viewport={{ once: true, margin: "100px" }}
                  onClick={() => openGallery(globalIndex)}
                  onMouseEnter={() => preloadOnHover(globalIndex)}
                >
                  <MediaContent>
                    {media.type === 'image' ? (
                      <FastOptimizedImage
                        src={media.thumbnailUrl || media.localUrl}
                        alt={media.caption}
                        thumbnail={true}
                        height="100%"
                        objectFit="cover"
                        style={{ transition: 'transform 0.3s ease' }}
                      />
                    ) : (
                      <VideoPlaceholder>
                        {media.thumbnailUrl ? (
                          <>
                            <FastOptimizedImage
                              src={media.thumbnailUrl}
                              alt={media.caption}
                              thumbnail={true}
                              height="100%"
                              objectFit="cover"
                              style={{ width: '100%' }}
                            />
                            <div className="play-overlay">
                              <div className="play-button">▶</div>
                            </div>
                          </>
                        ) : (
                          <>
                            
                            <div className="text">Video - Click to Play</div>
                          </>
                        )}
                      </VideoPlaceholder>
                    )}
                  </MediaContent>
                  
                  <MediaCaption>
                    <h4>{media.type === 'video' ? '' : ''}{media.caption}</h4>
                    <p>Click to {media.type === 'video' ? 'watch video' : 'view larger'}</p>
                  </MediaCaption>
                </MediaItem>
              );
            })}
          </MediaGrid>
        </CategorySection>
      ))}


      {/* React Image Gallery Modal */}
      {isGalleryOpen && (
        <GalleryModalOverlay onClick={closeGallery}>
          <GalleryContainer onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeGallery}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '1.5rem',
                cursor: 'pointer',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            <ImageGallery
              items={galleryItems}
              startIndex={galleryIndex}
              showThumbnails={true}
              showFullscreenButton={false}
              showPlayButton={false}
              slideDuration={300}
              slideInterval={0}
              renderItem={(item) => {
                if (item.isVideo) {
                  return (
                    <div style={{ textAlign: 'center' }}>
                      {item.isYoutube ? (
                        <iframe
                          width="100%"
                          height="400"
                          src={item.videoSrc
                            .replace('youtu.be/', 'www.youtube.com/embed/')
                            .replace('watch?v=', 'embed/')
                            .replace('youtube.com/shorts/', 'www.youtube.com/embed/')
                            .replace('www.youtube.com/shorts/', 'www.youtube.com/embed/')}
                          title={item.description}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{
                            maxHeight: '70vh',
                            maxWidth: '100%',
                            borderRadius: '8px'
                          }}
                        />
                      ) : (
                        <video
                          controls
                          style={{
                            maxHeight: '70vh',
                            maxWidth: '100%',
                            borderRadius: '8px'
                          }}
                          src={item.videoSrc}
                        />
                      )}
                      {item.description && (
                        <div className="image-gallery-description">
                          {item.description}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  // Custom image rendering to handle URL encoding issues
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <img
                        src={item.original}
                        alt={item.description}
                        style={{
                          maxHeight: '70vh',
                          maxWidth: '100%',
                          borderRadius: '8px'
                        }}
                        onError={(e) => {
                          console.error('Custom image failed to load:', e.target.src);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiPkltYWdlIEZhaWxlZCB0byBMb2FkPC90ZXh0Pjwvc3ZnPg==';
                        }}
                        onLoad={() => {
                          // Custom image loaded successfully
                        }}
                      />

                      {/* Download button for images */}
                      {item.filename && (
                        <div style={{ margin: '1rem 0' }}>
                          <DownloadButton
                            category={`Eliza's Award ${item.section || 'ceremony'}`}
                            filename={item.filename}
                            caption={item.description}
                            onError={(error) => console.error('Download error:', error)}
                          />
                        </div>
                      )}

                      {item.description && (
                        <div className="image-gallery-description">
                          {item.description}
                        </div>
                      )}
                    </div>
                  );
                }
              }}
            />
          </GalleryContainer>
        </GalleryModalOverlay>
      )}

      <NextSectionContainer>
        {/* <NextSectionText>
          Continue exploring more photos and the children's perspectives...
        </NextSectionText> */}
        <NextSectionButton href="/gallery">
          View Photos & Kids' Stories →
        </NextSectionButton>
      </NextSectionContainer>
    </AwardContainer>
  );
};

export default ElizasAward;
